import Icon from "../../common/Icon/Icon";
import type React from "react";
import { useMemo } from "react";
import type { GraphNode } from "../../../utils";
import { NodeDisplayType, getNodeDisplayData } from "../../../utils/NodeDisplayUtils";
import AttributeName from "../AttributeName/AttributeName";
import AttributeValue from "../AttributeValue/AttributeValue";
import Button from "../../common/Button/Button";
import DropdownMenu from "../../common/DropdownMenu/DropdownMenu";
import Chip from "../../common/Chip/Chip";
import styles from "./GraphData.module.scss";

export type GraphDataListItemProps = {
  id: string;
  graph: any;
  selectedNodeId: string | null;
  onSelectNode?: (node: GraphNode) => void;
  renderExtraMenuItems?: (node: GraphNode) => React.ReactNode;
  /** When false (non-debug), value is not shown (always unknown) */
  debug?: boolean;
};

const showOnHoverClsnm = "showOnHover-rt9861q6";

function GraphDataListItem(props: GraphDataListItemProps) {
  const { id, graph, selectedNodeId, onSelectNode, renderExtraMenuItems, debug = true } = props;

  // id could be a path (like "pay_records/2") or a GUID
  // Try to find the node - first try as GUID, then try to find by path
  const node = useMemo(() => {
    if (!graph?.node) {
      return null;
    }

    // First try as GUID
    let foundNode = graph.node(id);
    if (foundNode) {
      return foundNode;
    }

    // If not found, try to find by iterating all nodes and matching path
    if (graph.nodes) {
      for (const nodeId of graph.nodes()) {
        const n = graph.node(nodeId);
        if (n && (n.path === id || n.id === id)) {
          return n;
        }
      }
    }

    return null;
  }, [graph, id]);

  // Check if selected - compare both id and node.id/node.path
  const isSelected = useMemo(() => {
    if (!selectedNodeId || !node) {
      return false;
    }
    return selectedNodeId === id || selectedNodeId === node.id || selectedNodeId === node.path;
  }, [selectedNodeId, id, node]);

  const displayData = getNodeDisplayData(node);
  const extraMenuItems = useMemo(() => {
    if (!renderExtraMenuItems || !node) return null;
    return renderExtraMenuItems(node);
  }, [renderExtraMenuItems, node]);

  const warnings = (node?.warnings as number) || 0;
  const errors = (node?.errors as number) || 0;
  const containsWarnings = Boolean((node as any)?.containsWarnings);
  const containsErrors = Boolean((node as any)?.containsErrors);

  const greyedOut = displayData.type === NodeDisplayType.UNCERTAIN || displayData.type === NodeDisplayType.UNKNOWN;

  return (
    <div
      className={styles.listItem}
      data-deci-part="graphdata-listitem"
      data-selected={isSelected ? "true" : undefined}
      data-greyed={greyedOut ? "true" : undefined}
    >
      <div
        className={[
          styles.attr,
          node ? styles.attrClickable : "",
          renderExtraMenuItems ? styles.attrMenuPadding : "",
        ].join(" ")}
        data-deci-part="graphdata-attr"
        onClick={() => {
          if (node && onSelectNode) {
            onSelectNode(node);
          }
        }}
      >
        <AttributeName node={node} />
        {debug ? (
          <div className={styles.value}>
            <AttributeValue node={node} />
          </div>
        ) : null}
      </div>

      <div className={styles.badges}>
        {warnings > 0 ? (
          <Chip data-deci-part="graphdata-badge" size="sm" variant="warning" className={styles.badge}>
            <Chip.Label>{warnings}</Chip.Label>
          </Chip>
        ) : null}
        {errors > 0 ? (
          <Chip data-deci-part="graphdata-badge" size="sm" variant="destructive" className={styles.badge}>
            <Chip.Label>{errors}</Chip.Label>
          </Chip>
        ) : null}
        {(containsErrors || containsWarnings) && errors === 0 && warnings === 0 ? (
          <div
            data-deci-part="graphdata-dot"
            data-variant={containsErrors ? "error" : "warning"}
          />
        ) : null}

        {extraMenuItems ? (
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button icon
                className={showOnHoverClsnm}
                label="More"
                style={{ flexShrink: 0 }}
              >
                <Icon name="show-more" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {extraMenuItems}
            </DropdownMenu.Content>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}

export default GraphDataListItem;
